import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { Alert, Card, CardBody, CardHeader, EmptyState, Input, Skeleton } from '../../components/ui'
import { DocIcon } from '../../components/icons'

interface ActivityLogEntry {
  action?: string
  timestamp?: string
  created_at?: string
  _user_uid?: string
  [key: string]: unknown
}

export default function AuditTrail() {
  const [uidFilter, setUidFilter] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'audit-trail', uidFilter],
    queryFn: () => api.get<{ ok: boolean; items: ActivityLogEntry[] }>(
      `/gov/audit-trail${uidFilter ? `?uid=${encodeURIComponent(uidFilter)}` : ''}`,
    ),
  })

  const logs = data?.items ?? []
  const grouped = new Map<string, ActivityLogEntry[]>()
  for (const log of logs) {
    const ts = log.timestamp ?? log.created_at ?? ''
    const key = ts.slice(0, 10) || 'Unknown date'
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(log)
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Audit trail</h1>
          <p className="page-head__sub">Platform-wide activity log.</p>
        </div>
      </div>

      <div style={{ marginBottom: 'var(--sp-5)', maxWidth: 360 }}>
        <Input
          label="Filter by user ID"
          placeholder="Leave blank to see everyone"
          value={uidFilter}
          onChange={(e) => setUidFilter(e.target.value)}
        />
      </div>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
      ) : logs.length === 0 ? (
        <Card><CardBody><EmptyState icon={<DocIcon size={22} />} title="No activity found" body="" /></CardBody></Card>
      ) : (
        [...grouped.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([date, entries]) => (
          <Card key={date} style={{ marginBottom: 'var(--sp-5)' }}>
            <CardHeader title={date} />
            <CardBody flush>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Time</th><th>User</th><th>Action</th></tr></thead>
                  <tbody>
                    {entries.map((log, i) => (
                      <tr key={i}>
                        <td>{formatDateTime(log.timestamp ?? log.created_at)}</td>
                        <td>{log._user_uid || '—'}</td>
                        <td>{log.action || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        ))
      )}
    </>
  )
}
